/**
 * The part of the window the on-screen keyboard has not covered.
 *
 * `100vh` is the height of the window with every browser chrome hidden, which
 * is never the height available to lay something out in on a phone. `100dvh`
 * fixes the toolbar half of that, and on Android it fixes the keyboard too,
 * because the keyboard resizes the layout viewport there. iOS does not: the
 * keyboard slides over the page, the layout viewport keeps its full height, and
 * a full-screen composer laid out to `100dvh` puts its Send button underneath
 * the keys -- with nothing on screen to say the button exists.
 *
 * `visualViewport` is the only thing that reports the covered region. Its
 * height and offset are published as custom properties, so the CSS can size the
 * one element that must never be covered against them and fall back to `100dvh`
 * where the API is missing.
 *
 * The offset matters as much as the height: iOS scrolls the visual viewport up
 * to keep the focused field visible, which moves the page under a `position:
 * fixed` element rather than moving the element. Anchoring to `--vv-top` is
 * what keeps the composer aligned with what the reader can actually see.
 */

export function watchViewport(): void {
  const vv = window.visualViewport;
  if (!vv) return;

  let frame = 0;
  const apply = (): void => {
    frame = 0;
    const root = document.documentElement.style;
    root.setProperty("--vv-height", `${Math.round(vv.height)}px`);
    root.setProperty("--vv-top", `${Math.round(vv.offsetTop)}px`);
  };
  // The keyboard animates in, firing a resize per frame. One write per frame
  // is enough, and coalescing them keeps the composer from re-laying out
  // several times for the same gesture.
  const schedule = (): void => {
    if (frame === 0) frame = requestAnimationFrame(apply);
  };

  vv.addEventListener("resize", schedule);
  vv.addEventListener("scroll", schedule);
  apply();
}
